import React, { useState, useEffect } from 'react';
import { Row, Col, Card, Button, Table, Form, Modal, Badge, Spinner, Alert, Pagination } from 'react-bootstrap';
import apiService from '../services/api';
import PlatformUserPasswordResetModal from './PlatformUserPasswordResetModal';

const PlatformUsers = () => {
  const [users, setUsers] = useState([]);
  const [organizations, setOrganizations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [search, setSearch] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  // Helper to check if Save should be enabled (for Add User)
  const isSaveEnabled = () => {
    if (editingUser) return true;
    return (
      formData.organizationId &&
      formData.name.trim() &&
      formData.mobile.trim()
    );
  };
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [saving, setSaving] = useState(false);
  const [selectedOrganization, setSelectedOrganization] = useState('');
  const [statusFilter, setStatusFilter] = useState(''); // All/Active/Not Active
  const [lockedFilter, setLockedFilter] = useState(''); // All/Locked/Not Locked
  const [passwordChangeFilter, setPasswordChangeFilter] = useState(''); // All/Required/Not Required
  const [showPasswordResetModal, setShowPasswordResetModal] = useState(false);
  const [passwordResetData, setPasswordResetData] = useState(null);

  // Password reset confirmation modal state
  const [showPasswordResetConfirmModal, setShowPasswordResetConfirmModal] = useState(false);
  const [passwordResetConfirmData, setPasswordResetConfirmData] = useState(null);

  // Sorting state
  const [sortField, setSortField] = useState('');
  const [sortDirection, setSortDirection] = useState('asc'); // 'asc' or 'desc'

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    username: '',
    mobile: '',
    password: '',
    organizationId: '',
    category: 'customer',
    isActive: true,
    isLocked: false,
    isPerson: true
  });

  useEffect(() => {
    loadInitialData();
  }, []);

  useEffect(() => {
    loadUsers();
  }, [currentPage, search, itemsPerPage, selectedOrganization, statusFilter, lockedFilter, passwordChangeFilter, sortField, sortDirection]);

  const loadInitialData = async () => {
    try {
      const orgResponse = await apiService.getOrganizations(1, 1000); // Get all organizations for dropdown
      if (orgResponse.success) {
        setOrganizations(orgResponse.data || []);
      }
    } catch (error) {
      console.error('Error loading organizations:', error);
    }
  };

  const loadUsers = async () => {
    setLoading(true);
    setError('');
    console.log('Loading users with params:', {
      selectedOrganization, currentPage, itemsPerPage, search
    });
    try {
      // Use 'customer' as default category, but filter by organization if selected
      const response = await apiService.getPlatformUsers('customer', currentPage, itemsPerPage, search);
      if (response.success) {
        // Ensure users is always an array
        let userData = response.data || [];
        console.log('Users loaded:', userData);

        // Filter by organization if one is selected
        if (selectedOrganization) {
          userData = userData.filter(user => user.organization_id === parseInt(selectedOrganization));
        }

        // Apply status filter
        if (statusFilter === 'active') {
          userData = userData.filter(user => user.is_active === true);
        } else if (statusFilter === 'inactive') {
          userData = userData.filter(user => user.is_active === false);
        }

        // Apply locked filter
        if (lockedFilter === 'locked') {
          userData = userData.filter(user => user.is_locked === true);
        } else if (lockedFilter === 'unlocked') {
          userData = userData.filter(user => user.is_locked !== true);
        }

        // Apply password change filter
        if (passwordChangeFilter === 'required') {
          userData = userData.filter(user => user.password_change_required === true);
        } else if (passwordChangeFilter === 'not_required') {
          userData = userData.filter(user => user.password_change_required !== true);
        }

        // Apply sorting before setting users
        const sortedData = sortData(Array.isArray(userData) ? userData : []);
        setUsers(sortedData);
        console.log('Pagination metadata:', response.pagination);
        // Update total records/pages based on filtered results if any filter is applied
        const hasFilters = selectedOrganization || statusFilter || lockedFilter || passwordChangeFilter;
        const apiTotal =
          response.pagination?.total_records ??
          response.pagination?.total ??
          response.pagination?.count ??
          response.total ??
          (Array.isArray(response.data) ? response.data.length : 0);
        const apiPages =
          response.pagination?.total_pages ??
          response.pagination?.pages ??
          (apiTotal ? Math.max(1, Math.ceil(apiTotal / itemsPerPage)) : 1);
        const totalCount = hasFilters ? sortedData.length : apiTotal;
        setTotalRecords(totalCount);
        setTotalPages(hasFilters ? Math.max(1, Math.ceil(totalCount / itemsPerPage)) : apiPages);
      } else {
        setError(response.error || 'Failed to load users');
        setUsers([]); // Reset to empty array on error
      }
    } catch (error) {
      console.error('Error loading users:', error);
      setError('Failed to load users');
      setUsers([]); // Reset to empty array on error
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    if (e.key === 'Enter' || e.type === 'blur') {
      setCurrentPage(1);
      setSearch(e.target.value);
    }
  };

  const handleOrganizationChange = (organizationId) => {
    setSelectedOrganization(organizationId);
    setCurrentPage(1);
  };

  const handleShowModal = (user = null) => {
    if (user) {
      setEditingUser(user);
      setFormData({
        name: user.name || '',
        username: user.username || '',
        mobile: user.mobile || '',
        password: '', // Never populate password field
        organizationId: user.organization_id || '',
        category: user.is_person ? 'customer' : 'enterprise',
        isActive: user.is_active !== false,
        isLocked: user.is_locked === true,
        isPerson: user.is_person !== false
      });
    } else {
      setEditingUser(null);
      setFormData({
        name: '',
        email: '',
        username: '',
        mobile: '',
        password: '',
        organizationId: '',
        category: 'customer',
        isActive: true,
        isLocked: false,
        isPerson: true
      });
    }
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingUser(null);
    setFormData({
      name: '',
      email: '',
      username: '',
      mobile: '',
      password: '',
      organizationId: '',
      category: 'customer',
      isActive: true,
      isLocked: false,
      isPerson: true
    });
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSave = async (e) => {
    e.preventDefault();

    setSaving(true);
    setError('');

    // Form validation
    const newErrors = {};
    const { name, mobile } = formData;

    // Name validation
    if (!name.trim()) {
      newErrors.name = 'Name is required';
    } else if (name.trim().length < 3) {
      newErrors.name = 'Name must be at least 3 characters long';
    } else if (name.trim().length > 100) {
      newErrors.name = 'Name must be less than 100 characters';
    }

    // Mobile validation
    if (!mobile.trim()) {
      newErrors.mobile = 'Mobile number is required';
    } else if (!/^\d{9}$/.test(mobile.trim())) {
      newErrors.mobile = 'Mobile number must be exactly 9 digits (without country code)';
    } else if (/^0/.test(mobile.trim())) {
      newErrors.mobile = 'Mobile number should not start with 0 (country code +254 is already included)';
    }

    // Check if there are any validation errors
    if (Object.keys(newErrors).length > 0) {
      const errorMessages = Object.values(newErrors).join('. ');
      setError(errorMessages);
      setSaving(false);
      return;
    }

    try {
      const userData = {
        name: formData.name.trim(),
        password: formData.password,
        is_active: formData.isActive,
        is_locked: formData.isLocked,
        is_person: formData.isPerson
      };

      if (!formData.organizationId) {
        setError('Organization is required for customer users');
        setSaving(false);
        return;
      }
      userData.organization_id = formData.organizationId;
      userData.username = formData.username.trim();
      userData.mobile = formData.mobile.trim();


      let response;
      if (editingUser) {
        // Remove password if empty (don't update password)
        if (!userData.password) {
          delete userData.password;
        }
        response = await apiService.updatePlatformUser(editingUser.id, userData);
      } else {
        if (!userData.password) {
          setError('Password is required for new users');
          setSaving(false);
          return;
        }
        response = await apiService.createPlatformUser(userData);
      }

      if (response.success) {
        handleCloseModal();
        await loadUsers();
      } else {
        setError(response.error || 'Failed to save user');
      }
    } catch (error) {
      console.error('Error saving user:', error);
      setError('Failed to save user');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleStatus = async (id, isActive) => {
    try {
      const response = await apiService.togglePlatformUserStatus(id, isActive);
      if (response.success) {
        await loadUsers();
      } else {
        setError(response.error || 'Failed to update user status');
      }
    } catch (error) {
      console.error('Error toggling user status:', error);
      setError('Failed to update user status');
    }
  };

  const handleLockUser = async (user) => {
    const action = user.is_locked ? 'unlock' : 'lock';
    const confirmMessage = `Are you sure you want to ${action} user "${user.name}"?`;

    if (!window.confirm(confirmMessage)) {
      return;
    }

    try {
      const currentUser = JSON.parse(localStorage.getItem('user'));
      if (!currentUser || !currentUser.id) {
        setError('Unable to determine current user');
        return;
      }

      const response = await apiService.put(`/user_account/lock/${user.id}`, {
        is_locked: !user.is_locked,
        last_updated_by: currentUser.id
      });

      if (response.success) {
        await loadUsers();
      } else {
        setError(response.error || 'Failed to update user lock status');
      }
    } catch (error) {
      console.error('Error locking/unlocking user:', error);
      setError('Failed to update user lock status');
    }
  };

  const handleArchiveUser = async (user) => {
    if (!window.confirm(`Are you sure you want to archive user "${user.name}"? This action cannot be undone.`)) {
      return;
    }

    try {
      const currentUser = JSON.parse(localStorage.getItem('user'));
      if (!currentUser || !currentUser.id) {
        setError('Unable to determine current user');
        return;
      }

      const response = await apiService.makeRequest(`/user_account/${user.id}`, {
        method: 'DELETE',
        body: JSON.stringify({
          last_updated_by: currentUser.id
        })
      });

      if (response.success) {
        await loadUsers();
      } else {
        setError(response.error || 'Failed to archive user');
      }
    } catch (error) {
      console.error('Error archiving user:', error);
      setError('Failed to archive user');
    }
  };

  // Password reset confirmation handlers
  const handlePasswordResetConfirmation = (user_account, requirePasswordChange) => {
    performPasswordReset(user_account, requirePasswordChange);
    setShowPasswordResetConfirmModal(false);
    setPasswordResetConfirmData(null);
  };

  const handlePasswordResetCancel = () => {
    setShowPasswordResetConfirmModal(false);
    setPasswordResetConfirmData(null);
  };

  // Reset password for staff account
  const resetPassword = async (user_account) => {
    const confirmMessage = `Are you sure you want to reset the password for ${user_account.name}? A new temporary password will be generated.`;

    if (!window.confirm(confirmMessage)) {
      return;
    }

    // Show modal to ask if the user should be required to change password at first login
    setPasswordResetConfirmData(user_account);
    setShowPasswordResetConfirmModal(true);
  };

  // Perform the actual password reset
  const performPasswordReset = async (user_account, requirePasswordChange) => {
    try {
      const payload = {
        user_account_id: user_account.id,
        password_change_required: requirePasswordChange
      };
      console.log('Resetting password using payload:', payload);
      const response = await apiService.post('/user_account_password/reset', payload);
      console.log('Reset outcome', response);

      // Show the new reset password to the admin
      if (response.data && response.data.reset_password) {
        setPasswordResetData({
          userAccount: user_account,
          resetPassword: response.data.reset_password
        });
        setShowPasswordResetModal(true);
      } else {
        const changeRequiredText = requirePasswordChange
          ? "The user will be required to change their password at first login."
          : "The user can use the new password as-is.";
        alert(`Password has been reset successfully! A new temporary password has been generated for ${user_account.name}. ${changeRequiredText}`);
      }

      // Refresh the table after successful password reset
      await loadUsers();
    } catch (error) {
      console.error('Error resetting password:', error);
      alert('Error resetting password. Please try again.');
    }
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleItemsPerPageChange = (e) => {
    setItemsPerPage(parseInt(e.target.value));
    setCurrentPage(1);
  };

  // Sorting functions
  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const sortData = (data) => {
    if (!sortField) return data;

    return [...data].sort((a, b) => {
      let aVal = a[sortField];
      let bVal = b[sortField];

      // Handle special cases
      if (sortField === 'organization') {
        aVal = a.organization || '';
        bVal = b.organization || '';
      } else if (sortField === 'created_at' || sortField === 'last_updated_at' || sortField === 'last_login') {
        aVal = aVal ? new Date(aVal) : new Date(0);
        bVal = bVal ? new Date(bVal) : new Date(0);
      } else if (sortField === 'is_active') {
        aVal = a.is_active ? 1 : 0;
        bVal = b.is_active ? 1 : 0;
      } else {
        // Convert to string for consistent comparison
        aVal = (aVal || '').toString().toLowerCase();
        bVal = (bVal || '').toString().toLowerCase();
      }

      if (aVal < bVal) {
        return sortDirection === 'asc' ? -1 : 1;
      }
      if (aVal > bVal) {
        return sortDirection === 'asc' ? 1 : -1;
      }
      return 0;
    });
  };

  const getSortIcon = (field) => {
    if (sortField !== field) {
      return <i className="bi bi-arrow-down-up ms-1 text-muted"></i>;
    }
    return sortDirection === 'asc' ?
      <i className="bi bi-arrow-up ms-1 text-primary"></i> :
      <i className="bi bi-arrow-down ms-1 text-primary"></i>;
  };

  const isCustomerUser = formData.category === 'customer';

  return (
    <div>
      <Row>
        <Col>
          <div className="d-flex justify-content-between align-items-center mb-4">
            <h2>
              <i className="bi bi-people me-2"></i>
              Platform Users
            </h2>
            <Button variant="primary" onClick={() => handleShowModal()}>
              <i className="bi bi-person-plus me-2"></i>
              Add User
            </Button>
          </div>
        </Col>
      </Row>

      {error && (
        <Row className="mb-4">
          <Col>
            <Alert variant="danger" dismissible onClose={() => setError('')}>
              <i className="bi bi-exclamation-triangle me-2"></i>
              {error}
            </Alert>
          </Col>
        </Row>
      )}

      {/* Search and Filter */}
      <Card className="mb-4" style={{ border: '1px solid #282425' }}>
        <Card.Header style={{
          backgroundColor: '#282425',
          color: '#fcfcfc',
          borderBottom: '2px solid #662628'
        }}>
          <h6 className="mb-0">
            <i className="bi bi-funnel me-2" style={{ color: '#e42b2e' }}></i>
            Search & Filter Options
          </h6>
        </Card.Header>
        <Card.Body style={{ backgroundColor: '#fcfcfc' }}>
          {/* First Row - Search and Organization */}
          <Row className="mb-3">
            <Col md={6}>
              <Form.Group>
                <Form.Label style={{ color: '#282425', fontWeight: '600' }}>
                  <i className="bi bi-search me-2" style={{ color: '#662628' }}></i>
                  Search Users
                </Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Search by name, email, mobile..."
                  onKeyDown={handleSearch}
                  style={{
                    borderColor: '#662628',
                    focusBorderColor: '#e42b2e',
                    backgroundColor: '#fcfcfc'
                  }}
                  onFocus={(e) => e.target.style.borderColor = '#e42b2e'}
                  onBlur={(e) => {
                    handleSearch(e);
                    e.target.style.borderColor = '#662628';
                  }}
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group>
                <Form.Label style={{ color: '#282425', fontWeight: '600' }}>
                  <i className="bi bi-building me-2" style={{ color: '#662628' }}></i>
                  Organization Filter
                </Form.Label>
                <Form.Select
                  value={selectedOrganization}
                  onChange={(e) => handleOrganizationChange(e.target.value)}
                  style={{
                    borderColor: '#662628',
                    backgroundColor: '#fcfcfc'
                  }}
                  onFocus={(e) => e.target.style.borderColor = '#e42b2e'}
                  onBlur={(e) => e.target.style.borderColor = '#662628'}
                >
                  <option value="">All Organizations</option>
                  {organizations.map(org => (
                    <option key={org.id} value={org.id}>
                      {org.name} ({org.code})
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
          </Row>

          {/* Second Row - Status Filters */}
          <Row>
            <Col md={4}>
              <Form.Group>
                <Form.Label style={{ color: '#282425', fontWeight: '600' }}>
                  <i className="bi bi-check-circle me-2" style={{ color: '#662628' }}></i>
                  Status Filter
                </Form.Label>
                <Form.Select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  style={{
                    borderColor: '#662628',
                    backgroundColor: '#fcfcfc'
                  }}
                  onFocus={(e) => e.target.style.borderColor = '#e42b2e'}
                  onBlur={(e) => e.target.style.borderColor = '#662628'}
                >
                  <option value="">All</option>
                  <option value="active">Active Only</option>
                  <option value="inactive">Not Active Only</option>
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group>
                <Form.Label style={{ color: '#282425', fontWeight: '600' }}>
                  <i className="bi bi-lock me-2" style={{ color: '#662628' }}></i>
                  Lock Status
                </Form.Label>
                <Form.Select
                  value={lockedFilter}
                  onChange={(e) => setLockedFilter(e.target.value)}
                  style={{
                    borderColor: '#662628',
                    backgroundColor: '#fcfcfc'
                  }}
                  onFocus={(e) => e.target.style.borderColor = '#e42b2e'}
                  onBlur={(e) => e.target.style.borderColor = '#662628'}
                >
                  <option value="">All</option>
                  <option value="locked">Locked Only</option>
                  <option value="unlocked">Not Locked Only</option>
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group>
                <Form.Label style={{ color: '#282425', fontWeight: '600' }}>
                  <i className="bi bi-key me-2" style={{ color: '#662628' }}></i>
                  Password Change
                </Form.Label>
                <Form.Select
                  value={passwordChangeFilter}
                  onChange={(e) => setPasswordChangeFilter(e.target.value)}
                  style={{
                    borderColor: '#662628',
                    backgroundColor: '#fcfcfc'
                  }}
                  onFocus={(e) => e.target.style.borderColor = '#e42b2e'}
                  onBlur={(e) => e.target.style.borderColor = '#662628'}
                >
                  <option value="">All</option>
                  <option value="required">Password Change Required</option>
                  <option value="not_required">No Password Change Required</option>
                </Form.Select>
              </Form.Group>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      <Row>
        <Col>
          <Card>
            <Card.Header className="bg-white">
              <div className="d-flex justify-content-between align-items-center">
                <Badge bg="secondary" className="px-3 py-2" style={{ fontSize: '0.9rem' }}>
                  {totalRecords} Total Records
                </Badge>
                <div className="d-flex align-items-center gap-2">
                  <Form.Label className="mb-0 text-muted">Show:</Form.Label>
                  <Form.Select
                    size="sm"
                    value={itemsPerPage}
                    onChange={handleItemsPerPageChange}
                    style={{ width: 'auto' }}
                  >
                    <option value="10">10</option>
                    <option value="25">25</option>
                    <option value="50">50</option>
                  </Form.Select>
                  <span className="text-muted">per page</span>
                </div>
              </div>
            </Card.Header>
            <Card.Body className="p-0">
              {loading ? (
                <div className="text-center p-4">
                  <Spinner animation="border" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </Spinner>
                </div>
              ) : !Array.isArray(users) || users.length === 0 ? (
                <div className="text-center py-4 text-muted">
                  <i className="bi bi-people display-1"></i>
                  <div className="mt-2">No users found</div>
                </div>
              ) : (
                <Table responsive hover className="mb-0">
                  <thead>
                    <tr>
                      <th
                        style={{ cursor: 'pointer', userSelect: 'none' }}
                        onClick={() => handleSort('user_account_id')}
                      >
                        User Account ID {getSortIcon('user_account_id')}
                      </th>
                      <th
                        style={{ cursor: 'pointer', userSelect: 'none' }}
                        onClick={() => handleSort('name')}
                      >
                        Name {getSortIcon('name')}
                      </th>
                      <th
                        style={{ cursor: 'pointer', userSelect: 'none' }}
                        onClick={() => handleSort('organization')}
                      >
                        Organization {getSortIcon('organization')}
                      </th>
                      <th
                        style={{ cursor: 'pointer', userSelect: 'none' }}
                        onClick={() => handleSort('user_name')}
                      >
                        User Name {getSortIcon('user_name')}
                      </th>
                      <th
                        style={{ cursor: 'pointer', userSelect: 'none' }}
                        onClick={() => handleSort('mobile')}
                      >
                        Mobile {getSortIcon('mobile')}
                      </th>
                      <th
                        style={{ cursor: 'pointer', userSelect: 'none' }}
                        onClick={() => handleSort('is_active')}
                      >
                        Status {getSortIcon('is_active')}
                      </th>
                      <th
                        style={{ cursor: 'pointer', userSelect: 'none' }}
                        onClick={() => handleSort('roles')}
                      >
                        Roles {getSortIcon('roles')}
                      </th>
                      <th
                        style={{ cursor: 'pointer', userSelect: 'none' }}
                        onClick={() => handleSort('last_login')}
                      >
                        Last Login {getSortIcon('last_login')}
                      </th>
                      <th
                        style={{ cursor: 'pointer', userSelect: 'none' }}
                        onClick={() => handleSort('created_at')}
                      >
                        Created {getSortIcon('created_at')}
                      </th>
                      <th
                        style={{ cursor: 'pointer', userSelect: 'none' }}
                        onClick={() => handleSort('last_updated_at')}
                      >
                        Last Updated {getSortIcon('last_updated_at')}
                      </th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Array.isArray(users) && users.map((user) => (
                      <tr key={user.id}>
                        <td>{user.user_account_id}</td>
                        <td>
                          <div className="fw-medium">
                            {user.name}
                            {user.category === 'system' && (
                              <Badge bg="info" className="ms-2" title="System User">
                                <i className="bi bi-gear-fill me-1"></i>
                                System
                              </Badge>
                            )}
                          </div>
                          {user.username && <small className="text-muted">@{user.username}</small>}
                        </td>
                        <td>
                          {user.organization

                          }
                        </td>
                        <td>
                          {user.user_name

                          }
                        </td>
                        <td>
                          {(user.mobile || 'N/A')

                          }
                        </td>
                        <td>
                          <div>
                            <Badge bg={user.is_active ? 'success' : 'secondary'} className="me-1">
                              {user.is_active ? 'Active' : 'Inactive'}
                            </Badge>
                            {user.is_locked && <Badge bg="danger" className="me-1">Locked</Badge>}
                            {user.password_change_required && <Badge bg="warning" className="me-1">Password Change Pending</Badge>}
                          </div>
                        </td>
                        <td>{user.roles}</td>
                        <td>{(user.last_login) ? apiService.formatDate(user.last_login) : 'N/A'}</td>
                        <td>{apiService.formatDate(user.created_at)}</td>
                        <td>{apiService.formatDate(user.last_updated_at)}</td>
                        <td>
                          {user.is_active ? (
                            <div className="d-flex gap-1">
                              <Button
                                variant="outline-secondary"
                                size="sm"
                                onClick={() => handleShowModal(user)}
                                title="Edit"
                              >
                                <i className="bi bi-pencil"></i>
                              </Button>
                              <Button
                                variant="outline-info"
                                size="sm"
                                onClick={() => resetPassword(user)}
                                title="Reset Password"
                              >
                                <i className="bi bi-key"></i> Reset Password
                              </Button>
                              {user.is_locked ? (
                                <Button
                                  variant="outline-success"
                                  size="sm"
                                  onClick={() => handleLockUser(user)}
                                  title="Unlock User"
                                >
                                  <i className="bi bi-unlock"></i> Unlock User
                                </Button>
                              ) : (
                                <Button
                                  variant="outline-danger"
                                  size="sm"
                                  onClick={() => handleLockUser(user)}
                                  title="Lock User"
                                >
                                  <i className="bi bi-lock"></i> Lock User
                                </Button>
                              )}
                              <Button
                                variant="outline-dark"
                                size="sm"
                                onClick={() => handleArchiveUser(user)}
                                title="Archive User"
                              >
                                <i className="bi bi-archive"></i> Archive User
                              </Button>
                            </div>
                          ) : (
                            <span className="text-muted">No actions available</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              )}
            </Card.Body>

            {/* Pagination */}
            {totalPages > 1 && (() => {
              const maxVisible = 5;
              let start = Math.max(1, currentPage - Math.floor(maxVisible / 2));
              let end = Math.min(totalPages, start + maxVisible - 1);
              if (end - start + 1 < maxVisible) {
                start = Math.max(1, end - maxVisible + 1);
              }
              const firstRecord = totalRecords === 0 ? 0 : ((currentPage - 1) * itemsPerPage) + 1;
              const lastRecord = Math.min(currentPage * itemsPerPage, totalRecords);

              return (
                <Card.Footer>
                  <div className="d-flex justify-content-between align-items-center">
                    <div className="text-muted">
                      Showing {firstRecord} to {lastRecord} of {totalRecords} records
                    </div>
                    <Pagination className="mb-0">
                      <Pagination.First
                        disabled={currentPage === 1}
                        onClick={() => handlePageChange(1)}
                      />
                      <Pagination.Prev
                        disabled={currentPage === 1}
                        onClick={() => handlePageChange(currentPage - 1)}
                      />
                      {start > 1 && <Pagination.Ellipsis disabled />}
                      {Array.from({ length: end - start + 1 }, (_, i) => start + i).map((page) => (
                        <Pagination.Item
                          key={page}
                          active={page === currentPage}
                          onClick={() => handlePageChange(page)}
                        >
                          {page}
                        </Pagination.Item>
                      ))}
                      {end < totalPages && <Pagination.Ellipsis disabled />}
                      <Pagination.Next
                        disabled={currentPage === totalPages}
                        onClick={() => handlePageChange(currentPage + 1)}
                      />
                      <Pagination.Last
                        disabled={currentPage === totalPages}
                        onClick={() => handlePageChange(totalPages)}
                      />
                    </Pagination>
                  </div>
                </Card.Footer>
              );
            })()}
          </Card>
        </Col>
      </Row>

      {/* Create/Edit Modal */}
      <Modal show={showModal} onHide={handleCloseModal} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            {editingUser ? 'Edit User' : 'Add User'}
          </Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSave}>
          <Modal.Body>
            {/* Removed User Category Selection */}

            {/* Organization selection required first */}
            {!editingUser && (
              <Form.Group className="mb-3">
                <Form.Label>Organization *</Form.Label>
                <Form.Select
                  name="organizationId"
                  value={formData.organizationId}
                  onChange={handleInputChange}
                  required
                >
                  <option value="">Select Organization</option>
                  {organizations.map(org => (
                    <option key={org.id} value={org.id}>
                      {org.name} ({org.code})
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            )}

            {/* Disable other fields until organization is selected */}
            <Row>
              <Col md={12}>
                <Form.Group className="mb-3">
                  <Form.Label>Full Name *</Form.Label>
                  <Form.Control
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    placeholder="Enter full name"
                    required
                    disabled={!formData.organizationId}
                  />
                </Form.Group>
              </Col>
            </Row>
            <Form.Group className="mb-3">
              <Form.Label>Mobile Number *</Form.Label>
              <div className="input-group">
                <span className="input-group-text">+254</span>
                <Form.Control
                  type="tel"
                  name="mobile"
                  value={formData.mobile}
                  onChange={handleInputChange}
                  placeholder="e.g 712345678"
                  required
                  disabled={!formData.organizationId}
                />
              </div>
            </Form.Group>

            {/* Enterprise User Fields */}
            {/* Removed Email and Is Person Option */}

            {/* Organization (read-only) */}
            {editingUser && formData.organizationId && (
              <Form.Group className="mb-3">
                <Form.Label>Organization</Form.Label>
                <Form.Control
                  type="text"
                  value={organizations.find(org => org.id === formData.organizationId)?.name || ''}
                  readOnly
                  plaintext
                />
              </Form.Group>
            )}

            {/* Username (read-only) */}
            {editingUser && formData.username && (
              <Form.Group className="mb-3">
                <Form.Label>Username</Form.Label>
                <Form.Control
                  type="text"
                  value={formData.username}
                  readOnly
                  plaintext
                />
              </Form.Group>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={handleCloseModal}>
              Cancel
            </Button>
            <Button variant="primary" type="submit" disabled={saving || !isSaveEnabled()}>
              {saving ? (
                <>
                  <Spinner as="span" animation="border" size="sm" role="status" className="me-2" />
                  Saving...
                </>
              ) : (
                'Save User'
              )}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Password Reset Modal */}
      <PlatformUserPasswordResetModal
        show={showPasswordResetModal}
        onClose={() => setShowPasswordResetModal(false)}
        userAccount={passwordResetData?.userAccount}
        resetPassword={passwordResetData?.resetPassword}
      />

      {/* Password Reset Confirmation Modal */}
      <Modal show={showPasswordResetConfirmModal} onHide={handlePasswordResetCancel} size="md" centered>
        <Modal.Header closeButton style={{ backgroundColor: '#f8f9fa', borderBottom: '2px solid #dee2e6' }}>
          <Modal.Title>
            <i className="bi bi-key-fill me-2 text-warning"></i>
            Password Reset Options
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ backgroundColor: '#ffffff' }}>
          {passwordResetConfirmData && (
            <div className="text-center">
              <div className="mb-4">
                <i className="bi bi-person-circle display-4 text-primary"></i>
              </div>
              <h5 className="mb-3">Password Change Requirement</h5>
              <p className="mb-4">
                Should <strong>{passwordResetConfirmData.name}</strong> be required to change their password at first login?
              </p>
              <Alert variant="info" className="mb-4">
                <div className="d-flex align-items-start">
                  <i className="bi bi-info-circle me-2 mt-1"></i>
                  <div>
                    <strong>Choose an option:</strong>
                    <ul className="mt-2 mb-0 text-start">
                      <li><strong>Yes:</strong> User must change password on first login (recommended for security)</li>
                      <li><strong>No:</strong> User can use the new temporary password as-is</li>
                    </ul>
                  </div>
                </div>
              </Alert>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer style={{ backgroundColor: '#f8f9fa', borderTop: '2px solid #dee2e6' }}>
          <div className="w-100 d-flex justify-content-center gap-3">
            <Button
              variant="outline-secondary"
              onClick={() => handlePasswordResetConfirmation(passwordResetConfirmData, false)}
              size="lg"
              style={{ minWidth: '140px' }}
            >
              <i className="bi bi-x-circle me-2"></i>
              No, Allow As-Is
            </Button>
            <Button
              variant="warning"
              onClick={() => handlePasswordResetConfirmation(passwordResetConfirmData, true)}
              size="lg"
              style={{ minWidth: '140px' }}
            >
              <i className="bi bi-check-circle me-2"></i>
              Yes, Require Change
            </Button>
          </div>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default PlatformUsers;
